import { t } from "@lingui/macro";
import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { ReactNode, useEffect, useRef, useState } from "react";
import {
  NativeSyntheticEvent,
  ScrollView,
  TextInput,
  TextInputKeyPressEventData,
} from "react-native";
import { useFragment } from "react-relay";

import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator/ClubStackNavigator";
import { Button, Input } from "@whoosh/design-system/src/components/controls";
import { PhoneInput } from "@whoosh/design-system/src/components/controls/PhoneInput";
import { buildFullPhoneNumber } from "@whoosh/design-system/src/components/controls/PhoneInput/utils/buildPhoneNumber";
import { getCountryCodeById } from "@whoosh/design-system/src/components/controls/PhoneInput/utils/countryCodes";
import { VStack, Text } from "@whoosh/design-system/src/components/primitive";

import { SessionClub } from "../../../../../../../../Session";
import { useCheckEmailorPhone } from "../../../../hooks/useCheckEmailOrPhone";
import { InitialStepAuthenticationModalFragment_Session$key } from "./__generated__/InitialStepAuthenticationModalFragment_Session.graphql";
import type { ContactInputPreference } from "./utils";
import {
  containsOnlyPhoneFormattingCharacters,
  getEffectiveInputMode,
  getEmailValueOnSwitch,
  getPhoneValueOnSwitch,
  isValidToContinue,
  normalizePhoneValue,
  stripPhoneFormattingCharacters,
} from "./utils";
import { splitCountryCodeFromInput } from "./utils/detectCountryCodeFromInput";

/**
 *
 * The initial step is responsible for accepting an email or phone number,
 * it sends that up to check whether we are going to move to entering a password,
 * or verifying our contact information.
 */

export const InitialStep = ({
  sessionRef,
  clubSlug,
  sessionClub,
  alternativeSignIn,
}: {
  sessionRef: InitialStepAuthenticationModalFragment_Session$key | null;
  clubSlug?: string;
  sessionClub?: SessionClub | null;
  alternativeSignIn?: ReactNode;
}) => {
  const session = useFragment(InitialStepFragmentString, sessionRef);
  const memberClub = session?.currentClientProfile?.member?.club;
  const authenticationClub = sessionClub ?? memberClub;
  const authenticationClubSlug = clubSlug ?? authenticationClub?.slug;
  const authenticationClubSupportsPublic =
    !clubSlug || clubSlug === authenticationClub?.slug
      ? authenticationClub?.supportsPublic
      : undefined;

  //extract an existing email or phone number from the route params
  const routeParams =
    useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>().params;
  const emailOrPhone = routeParams?.emailOrPhone || "";

  const [currentValue, setCurrentValue] = useState(emailOrPhone);
  const [inputPreference, setInputPreference] =
    useState<ContactInputPreference>("Automatic");

  const defaultCountryCodeId =
    session?.currentClientProfile?.member?.club?.defaultCountryCode;
  const defaultCountryCode = defaultCountryCodeId
    ? getCountryCodeById(defaultCountryCodeId)
    : null;

  const defaultCountryCodeString = defaultCountryCode?.code?.toString();

  const [currentCountryCode, setCurrentCountryCode] = useState<
    string | undefined
  >(undefined);

  // State to track the detected country ID for the phone input
  const [detectedCountryId, setDetectedCountryId] = useState<string | null>(
    null
  );

  const handleKeyPress = (
    e: NativeSyntheticEvent<TextInputKeyPressEventData> | KeyboardEvent
  ) => {
    const key = "nativeEvent" in e ? e.nativeEvent.key : e.key;
    if (key === "Backspace" && currentValue.length === 0) {
      setCurrentCountryCode(undefined);
    }
  };

  const effectiveMode = getEffectiveInputMode(
    currentValue,
    currentCountryCode,
    inputPreference
  );
  const canContinue = isValidToContinue(
    currentValue,
    currentCountryCode || defaultCountryCodeString || "1",
    effectiveMode
  );

  const [checkContactInfo, isChecking] = useCheckEmailorPhone({
    clubSlug: authenticationClubSlug,
    clubSupportsPublic: authenticationClubSupportsPublic,
  });

  const handleContinueClick = () => {
    if (canContinue && !isChecking) {
      checkContactInfo({
        value:
          effectiveMode === "Phone"
            ? buildFullPhoneNumber(
                currentCountryCode || defaultCountryCodeString || "1",
                normalizePhoneValue(currentValue)
              ) || ""
            : currentValue,
        type: effectiveMode === "Phone" ? "Phone" : "Email",
      });
    }
  };

  const inputRef = useRef<TextInput>(null);
  const currentValueRef = useRef(currentValue);
  currentValueRef.current = currentValue;

  useEffect(() => {
    // Automatically focus the input after swapping
    // this is necessary so that when we flip between phone/email, we don't lose focus.
    if (inputRef.current) {
      inputRef.current.focus();

      if (effectiveMode === "Email") {
        const cursorPosition = currentValueRef.current.length;
        inputRef.current.setNativeProps?.({
          selection: { start: cursorPosition, end: cursorPosition },
        });
      }
    }
  }, [effectiveMode]);

  const handleAutomaticTextChange = (text: string) => {
    // Remove all spaces from the text
    const textWithoutSpaces = text.replace(/\s/g, "");

    const cleanText = containsOnlyPhoneFormattingCharacters(textWithoutSpaces)
      ? stripPhoneFormattingCharacters(textWithoutSpaces)
      : textWithoutSpaces;

    // Move a typed "+countrycode" into the country selector so it isn't shown twice
    const detected = splitCountryCodeFromInput(cleanText);
    if (detected) {
      setDetectedCountryId(detected.countryId);
      setCurrentCountryCode(
        getCountryCodeById(detected.countryId).code.toString()
      );
      setCurrentValue(detected.nationalNumber);
      return;
    }

    setCurrentValue(cleanText);

    if (!cleanText.startsWith("+")) {
      // Reset detected country ID if user is not typing a country code
      setDetectedCountryId(null);
    }
  };

  const handleUseEmailInstead = () => {
    if (isChecking) {
      return;
    }

    setCurrentValue((value) => getEmailValueOnSwitch(value));
    setInputPreference("Email");
  };

  const handleUsePhoneInstead = () => {
    if (isChecking) {
      return;
    }

    setCurrentValue((value) => getPhoneValueOnSwitch(value));
    setInputPreference("Phone");
  };

  return (
    <VStack paddingHorizontal={6} flexGrow={1} flex={1}>
      <ScrollView style={{ flexGrow: 1 }} keyboardShouldPersistTaps="handled">
        <VStack mb={8}>
          <Text variant={"mainTitle2"}>{t`Sign in or Sign up`}</Text>
        </VStack>
        {effectiveMode === "Phone" ? (
          <PhoneInput
            placeholder={t`Mobile phone number`}
            nationalNumber={currentValue}
            defaultCountryId={
              detectedCountryId || currentCountryCode || defaultCountryCodeId
            }
            onChange={({ countryCode, nationalNumber }) => {
              setCurrentCountryCode(countryCode);
              handleAutomaticTextChange(nationalNumber);
            }}
            name={t`phone number`}
            inputRef={inputRef}
            testID="subtle-phone-input"
            onKeyPress={handleKeyPress}
          />
        ) : (
          <Input<"text">
            placeholder={
              inputPreference === "Email"
                ? t`Email address`
                : t`Email or mobile phone number`
            }
            autoComplete="username"
            name={inputPreference === "Email" ? t`email` : t`email or phone`}
            value={currentValue}
            onChange={
              inputPreference === "Email"
                ? setCurrentValue
                : handleAutomaticTextChange
            }
            inputRef={inputRef}
            keyboardType="email-address"
            spellCheck
            testID="initial-step-input"
          />
        )}
      </ScrollView>

      <VStack gap={3}>
        {effectiveMode === "Phone" ? (
          <VStack alignItems="center">
            <Button
              accessibilityLabel={t`Use email instead`}
              isDisabled={isChecking}
              onPress={handleUseEmailInstead}
              size="small"
              testID="use-email-instead-button"
              variant="ghost"
            >
              {t`Use email instead`}
            </Button>
          </VStack>
        ) : inputPreference === "Email" ? (
          <VStack alignItems="center">
            <Button
              accessibilityLabel={t`Use phone instead`}
              isDisabled={isChecking}
              onPress={handleUsePhoneInstead}
              size="small"
              testID="use-phone-instead-button"
              variant="ghost"
            >
              {t`Use phone instead`}
            </Button>
          </VStack>
        ) : null}
        <Button
          accessibilityLabel={t`Continue`}
          isLoading={isChecking}
          isDisabled={!canContinue}
          onPress={handleContinueClick}
          testID="initial-step-continue-button"
        >
          {t`Continue`}
        </Button>
        {alternativeSignIn}
      </VStack>
    </VStack>
  );
};

export const InitialStepFragmentString = graphql`
  fragment InitialStepAuthenticationModalFragment_Session on Session {
    currentClientProfile {
      member {
        club {
          defaultCountryCode
          slug
          supportsPublic
        }
      }
    }
  }
`;
